import { Form, Input, InputNumber, Select, type GetRef } from 'antd';
import type { Ref } from 'react';
import {
  CAPTURE_STEPS,
  CAPTURE_STEP_DESCRIPTIONS,
  stepAsksForFuel,
  stepAsksForPhotos,
  stepRequiresNote,
  type CaptureStep,
} from '../policies/capture-step.policy';

const STEP_OPTIONS = CAPTURE_STEPS.map((step) => ({ value: step, label: CAPTURE_STEP_DESCRIPTIONS[step].label }));
const PHOTO_TYPES = 'image/jpeg,image/png,image/webp';

interface DriverCaptureFieldsProps {
  step: CaptureStep | undefined;
  picker: Ref<GetRef<typeof Select>>;
  photoInputKey: number;
  onPhotos: (photos: File[]) => void;
}

/** The fields of one step, a single line each so Enter saves; what shows beyond step and time depends on the step. */
export function DriverCaptureFields({ step, picker, photoInputKey, onPhotos }: DriverCaptureFieldsProps) {
  const needsNote = Boolean(step && stepRequiresNote(step));
  return (
    <>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item name="step" label="Step" rules={[{ required: true, message: 'Choose the step' }]}>
          <Select ref={picker} options={STEP_OPTIONS} placeholder="Choose the step" />
        </Form.Item>
        <Form.Item name="when" label="When it happened" rules={[{ required: true, message: 'Say when it happened' }]}>
          <Input type="datetime-local" />
        </Form.Item>
      </div>
      {step && stepAsksForFuel(step) && (
        <div className="grid gap-x-4 sm:grid-cols-2">
          <Form.Item name="litres" label="Litres">
            <InputNumber className="w-full" min={0.01} precision={2} controls={false} />
          </Form.Item>
          <Form.Item name="station" label="Station">
            <Input />
          </Form.Item>
        </div>
      )}
      <Form.Item
        name="note"
        label={needsNote ? 'What happened' : 'Note'}
        rules={[{ required: needsNote, whitespace: true, message: 'Say what happened' }, { max: 500 }]}
      >
        <Input />
      </Form.Item>
      {step && stepAsksForPhotos(step) && (
        <Form.Item label="Photos">
          <input
            key={photoInputKey}
            type="file"
            multiple
            accept={PHOTO_TYPES}
            aria-label="Photos"
            onChange={(event) => onPhotos([...(event.target.files ?? [])])}
            className="block w-full text-sm text-muted"
          />
        </Form.Item>
      )}
    </>
  );
}
